import React, { useCallback, useState, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  LinearProgress,
  Chip,
  Alert,
  Button,
  List,
  ListItem,
  CircularProgress,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

import * as pdfjs from "pdfjs-dist";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

import { useDropzone } from "react-dropzone";
import { validateFile } from "@harness-serverless/file-validator";
import { useFileUpload } from "../../hooks/useFileUpload";
import { useLogFileError } from "../../hooks/useLogFileError";
import type {
  FileUploadItem,
  FileUploadStatus,
} from "../../hooks/useFileUpload";

interface FileUploadProps {
  jobId: string;
  onUploadComplete?: (files: FileUploadItem[]) => void;
  onError?: (error: Error) => void;
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  acceptedFileTypes?: string[];
}

const DEFAULT_MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

// Accept PDF files only
const DEFAULT_ACCEPTED_TYPES = ["application/pdf"];

export function FileUpload({
  jobId,
  onUploadComplete,
  onError,
  maxFiles = 100,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
}: FileUploadProps) {
  const {
    uploadQueue,
    activeUploads,
    completedUploads,
    failedUploads,
    duplicateUploads,
    addFiles,
    removeFile,
    retryFile,
    clearCompleted,
    isUploading,
  } = useFileUpload({
    jobId,
    maxConcurrentUploads: 2,
    onUploadComplete,
    onError,
  });

  const { logFileError } = useLogFileError();
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFiles = useCallback(
    async (files: File[]): Promise<{ valid: File[]; errors: string[] }> => {
      const valid: File[] = [];
      const errors: string[] = [];

      for (const file of files) {
        const validationResult = await validateFile(file, {
          maxFileSize,
          acceptedFileTypes,
        });
        if (validationResult.ok) {
          valid.push(file);
        } else {
          errors.push(validationResult.error);
          if (validationResult.errorType === "PasswordProtected") {
            logFileError(jobId, {
              fileName: file.name,
              errorType: "PasswordProtection",
              errorMessage: "PDF is password-protected.",
            });
          }
          if (validationResult.errorType === "CorruptedFile") {
            logFileError(jobId, {
              fileName: file.name,
              errorType: "CorruptedFile",
              errorMessage: "PDF is corrupted.",
            });
          }
          if (validationResult.errorType === "MagicByteMismatch") {
            logFileError(jobId, {
              fileName: file.name,
              errorType: "MagicByteMismatch",
              errorMessage: "File type check failed: wrong magic bytes.",
              details: validationResult.details,
            });
          }
        }
      }

      const totalFiles =
        uploadQueue.length +
        activeUploads.length +
        completedUploads.length +
        duplicateUploads.length +
        valid.length;
      if (totalFiles > maxFiles) {
        const excess = totalFiles - maxFiles;
        valid.splice(valid.length - excess, excess);
        errors.push(
          `Maximum ${maxFiles} files allowed. ${excess} file(s) not added.`,
        );
      }

      return { valid, errors };
    },
    [
      maxFiles,
      uploadQueue.length,
      activeUploads.length,
      completedUploads.length,
      duplicateUploads.length,
      jobId,
      logFileError,
    ],
  );

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const { valid, errors } = await validateFiles(acceptedFiles);

      if (valid.length > 0) {
        addFiles(valid);
      }

      if (errors.length > 0) {
        errors.forEach((error) => {
          onError?.(new Error(error));
        });
      }
    },
    [addFiles, validateFiles, onError],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    noClick: true,
    noKeyboard: true,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
    onDropAccepted: () => setDragActive(false),
    onDropRejected: () => setDragActive(false),
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    handleDrop(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getStatusIcon = (status: FileUploadStatus) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon color="success" />;
      case "failed":
        return <ErrorIcon color="error" />;
      case "duplicate":
        return <WarningIcon color="warning" />;
      case "uploading":
        return <CircularProgress size={20} />;
      default:
        return <FileIcon />;
    }
  };

  const getStatusColor = (
    status: FileUploadStatus,
  ): "default" | "primary" | "success" | "error" | "warning" => {
    switch (status) {
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "duplicate":
        return "warning";
      case "uploading":
        return "primary";
      default:
        return "default";
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const allFiles = [
    ...uploadQueue,
    ...activeUploads,
    ...completedUploads,
    ...failedUploads,
    ...duplicateUploads,
  ];

  return (
    <Box>
      <Paper
        {...getRootProps()}
        sx={{
          p: 3,
          mb: 2,
          border: "2px dashed",
          borderColor: dragActive || isDragActive ? "primary.main" : "divider",
          bgcolor:
            dragActive || isDragActive ? "action.hover" : "background.paper",
          cursor: "pointer",
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: "action.hover",
          },
        }}
      >
        <input {...getInputProps()} />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedFileTypes.join(",")}
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        <Box textAlign="center">
          <CloudUploadIcon
            sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
          />
          <Typography variant="h6" gutterBottom>
            Drag & drop files here
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            or
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ mb: 2 }}
          >
            Select Files
          </Button>
          <Typography variant="caption" display="block" color="text.secondary">
            PDF files only, up to 50 MB each.
          </Typography>
        </Box>
      </Paper>

      {allFiles.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Files ({allFiles.length})</Typography>
            {completedUploads.length > 0 && (
              <Button size="small" onClick={clearCompleted} color="inherit">
                Clear Completed
              </Button>
            )}
          </Box>

          <List>
            {allFiles.map((file) => (
              <ListItem key={file.id} divider>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    width: "100%",
                    gap: 2,
                  }}
                >
                  <Box sx={{ mt: 0.5 }}>{getStatusIcon(file.status)}</Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={file.file.name}>
                      {file.file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.file.size)}
                    </Typography>
                    {file.status === "uploading" && (
                      <LinearProgress
                        variant="determinate"
                        value={file.progress}
                        sx={{ width: "100%", mt: 0.5 }}
                      />
                    )}
                    {file.error && (
                      <Typography
                        variant="caption"
                        color="error"
                        display="block"
                      >
                        {file.error}
                      </Typography>
                    )}
                  </Box>

                  <Chip
                    label={file.status}
                    color={getStatusColor(file.status)}
                    size="small"
                  />

                  {file.status === "failed" ? (
                    <IconButton onClick={() => retryFile(file.id)} size="small">
                      <ErrorIcon />
                    </IconButton>
                  ) : file.status === "queued" ||
                    file.status === "duplicate" ? (
                    <IconButton
                      onClick={() => removeFile(file.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  ) : null}
                </Box>
              </ListItem>
            ))}
          </List>

          {isUploading && (
            <Alert
              severity="info"
              sx={{
                mt: 2,
                color: "grey.800",
                "& .MuiAlert-icon": {
                  color: "grey.800",
                },
              }}
            >
              Uploading {activeUploads.length} of{" "}
              {uploadQueue.length + activeUploads.length} files...
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
}
